<template>
    <v-container fluid v-if="item" :alvqa="alvqa">
        <v-row class="pl-4 pr-4" align="center">
            <v-col class="headline" :alvqa="alvqa + '-title'">
                {{ item.description || item.fileName }}
                <v-chip small label class="ml-2" :color="statusColor" text-color="white" :alvqa="alvqa + '-status'">
                    {{ $t(statusText) }}
                </v-chip>
            </v-col>
        </v-row>

        <v-row class="pl-4 pr-4">
<!-- IMPORT -->
            <v-col cols="12" md="4">
                <div class="subtitle-1 font-weight-medium ml-4">{{ $t('lang_GENERIC_CAPTION_IMPORT') }}</div>
                <BaseReadOnlyTextField :label="$t('lang_GENERIC_CAPTION_FILE')" :innerValue="item.fileName + (item.fileSize ? ' (' + fileSize + ')' : '')" :alvqa="alvqa + '-file'" />
                <BaseReadOnlyTextField :label="$t('lang_GENERIC_CAPTION_MAPPING')" :innerValue="item.mapping ? item.mapping.title : '–'" :alvqa="alvqa + '-mapping'" />
                <BaseReadOnlyTextField
                    :label="$t('lang_PUBLISHING_USAGE_CAPTION_DETAIL_ROWS_STORED')"
                    :innerValue="item.importSummary ? $n(item.importSummary.stored) + ' / ' + $t('lang_GENERIC_CAPTION_REJECTED') + ' ' + $n(item.importSummary.rejected) : $n(item.rowCount)"
                    :alvqa="alvqa + '-rows'"
                />
                <v-alert v-if="item.importError" type="error" dense text class="ml-4 mr-4" :alvqa="alvqa + '-import-error'">{{ item.importError }}</v-alert>
                <UsageEpisodeRederive v-if="item.importStatus === 'imported'" :datasetId="item.id" />
            </v-col>

<!-- COMPARISON (schedule) / USED BY (statement) -->
            <v-col cols="12" md="4">
                <template v-if="item.dataType === 'broadcast_schedule'">
                    <div class="subtitle-1 font-weight-medium ml-4">{{ $t('lang_PUBLISHING_USAGE_CAPTION_DETAIL_COMPARISON') }}</div>
                    <div v-if="!item.comparison" class="ml-4 mt-3 subtitleGray--text" :alvqa="alvqa + '-no-comparison'">
                        {{ $t('lang_PUBLISHING_USAGE_CAPTION_DETAIL_NOT_COMPARED') }}
                    </div>
                    <template v-else>
                        <BaseReadOnlyTextField
                            :label="$t('lang_PUBLISHING_USAGE_CAPTION_DETAIL_COMPARED_AT')"
                            :innerValue="comparisonState"
                            :alvqa="alvqa + '-compared-at'"
                        />
                        <BaseReadOnlyTextField
                            :label="$t('lang_PUBLISHING_USAGE_CAPTION_COMPARED_AGAINST')"
                            :innerValue="(item.comparison.statements || []).map(s => s.description || s.fileName).join(', ') || '–'"
                            :alvqa="alvqa + '-against'"
                        />
                        <div class="ml-4 mt-1" v-if="item.comparison.summary">
                            <OutcomeCounts :outcomes="item.comparison.summary.outcomes" hideEmpty :alvqa="alvqa" />
                        </div>
                        <v-alert v-if="item.comparison.error" type="error" dense text class="ml-4 mr-4" :alvqa="alvqa + '-comparison-error'">{{ item.comparison.error }}</v-alert>
                    </template>
                    <div class="ml-4 mt-2">
                        <BaseButton
                            v-if="item.comparison && item.comparison.status === 'completed'"
                            label="lang_PUBLISHING_USAGE_ACTION_REVIEW"
                            icon="fact_check"
                            class="mr-2"
                            :alvqa="alvqa + '-button-review'"
                            @click="$router.push({ name: 'UsageReview', params: { alvId: item.id } })"
                        />
                        <BaseButton
                            v-if="item.comparable"
                            :label="item.comparison ? 'lang_PUBLISHING_USAGE_BUTTON_COMPARE_AGAIN' : 'lang_PUBLISHING_USAGE_ACTION_COMPARE'"
                            color="buttonNewEntity"
                            icon="compare_arrows"
                            :disabled="comparisonBusy"
                            :alvqa="alvqa + '-button-compare'"
                            @click="$store.dispatch('openCompareUsage', item)"
                        />
                    </div>
                </template>
                <template v-else>
                    <div class="subtitle-1 font-weight-medium ml-4">{{ $t('lang_PUBLISHING_USAGE_CAPTION_DETAIL_USED_BY') }}</div>
                    <div v-if="!item.usedBy || !item.usedBy.length" class="ml-4 mt-3 subtitleGray--text" :alvqa="alvqa + '-unused'">
                        {{ $t('lang_PUBLISHING_USAGE_CAPTION_DETAIL_NOT_USED') }}
                    </div>
                    <ul v-else class="ml-4 mt-3" :alvqa="alvqa + '-used-by'">
                        <li v-for="use in item.usedBy" :key="use.comparisonId">
                            <a @click="$router.push({ name: 'UsageReview', params: { alvId: use.scheduleId } })">{{ use.scheduleLabel }}</a>
                        </li>
                    </ul>
                </template>
            </v-col>

<!-- DELETE -->
            <v-col cols="12" md="4">
                <div class="subtitle-1 font-weight-medium ml-4">{{ $t('lang_GENERIC_CAPTION_DELETE') }}</div>
                <div v-if="item.deletable" class="ml-4 mt-3" :alvqa="alvqa + '-deletable'">
                    {{ $t('lang_PUBLISHING_USAGE_CAPTION_DETAIL_DELETABLE') }}
                    <template v-if="item.deletesComparisons && item.deletesComparisons.length">{{ $t('lang_PUBLISHING_USAGE_CAPTION_DELETE_DIALOG_WITH_COMPARISON') }}</template>
                </div>
                <ul v-else class="ml-4 mt-3" :alvqa="alvqa + '-delete-blockers'">
                    <li v-for="(blocker, i) in item.deleteBlockers" :key="i">{{ blocker.message }}</li>
                </ul>
            </v-col>
        </v-row>

<!-- STATIONS -->
        <v-row class="pl-4 pr-4" v-if="item.importStatus === 'imported'">
            <v-col cols="12">
                <UsageStationsPanel :dataset="item" />
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import BaseReadOnlyTextField from '@/components/_readonly/BaseReadOnlyTextField'
import BaseButton from '@/components/_buttons/BaseButton'
import OutcomeCounts from '../_components/OutcomeCounts'
import UsageStationsPanel from './UsageStationsPanel'
import UsageEpisodeRederive from './UsageEpisodeRederive'
import { IMPORT_STATUSES } from '../usageOptions'

export default {
    name: 'UsageReadOnly',
    components: { BaseReadOnlyTextField, BaseButton, OutcomeCounts, UsageStationsPanel, UsageEpisodeRederive },
    props: {
        datasetId: { type: Number, required: true }
    },
    data() {
        return { alvqa: 'publishing-usage-readonly' }
    },
    computed: {
        item() { return this.$store.getters.getUsageDetails.find(d => d.id === this.datasetId) },
        statusText() { return IMPORT_STATUSES[this.item.importStatus] || this.item.importStatus },
        statusColor() {
            return { imported: 'buttonNewEntity', failed: 'error', importing: 'standardBrownIcon', deleting: 'standardBrownIcon' }[this.item.importStatus] || 'iconDelete'
        },
        fileSize() {
            const kb = this.item.fileSize / 1024
            return kb < 1024 ? this.$n(Math.round(kb)) + ' KB' : this.$n(Math.round(kb / 102.4) / 10) + ' MB'
        },
        comparisonBusy() { return this.item.comparison && ['queued', 'running'].includes(this.item.comparison.status) },
        comparisonState() {
            const c = this.item.comparison
            if (this.comparisonBusy) return this.$t('lang_PUBLISHING_USAGE_CAPTION_DETAIL_COMPARING')
            const at = c.finishedAt || c.queuedAt
            return at ? this.$d(new Date(at.replace(' ', 'T')), 'alvDateTime') : '–'
        }
    }
}
</script>
